import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { TFunction } from 'i18next';

import type { IdMeta } from '#components/mobile/travesia/catalogo';

import type { Recuerdo } from './recuerdos';

/**
 * Aspecto de la botella y del sello de una travesía: «variación sin azar»
 * (F1 §2). Cada atributo sale de un dato real y tiene una lectura literal,
 * nunca evaluativa. Los valores son los del prototipo `prototipo-botella.html`
 * (función `derivar`) para que la interfaz los pinte igual.
 *
 * Funciones puras, sin React. Los textos explicativos se piden con
 * `explicarAspecto(t, …)` (claves i18n en inglés).
 */

export type IdVidrio =
  | 'cobalto'
  | 'verde-botella'
  | 'ambar'
  | 'rosado-ahumado'
  | 'transparente';
export type Estacion = 'invierno' | 'primavera' | 'verano' | 'otono';
export type NivelPatina = 'limpia' | 'ligera' | 'opalina';
export type FormaSello =
  | 'circulo'
  | 'cuadrado'
  | 'octogono'
  | 'rombo'
  | 'ovalo';
/** 0 frasco · 1 baja · 2 mediana · 3 alta · 4 de cuello largo. */
export type Escalon = 0 | 1 | 2 | 3 | 4;

/** Vidrio por tipo de meta: [oscuro, medio, claro, borde]. */
export const VIDRIOS: Record<
  IdMeta,
  { id: IdVidrio; colores: readonly [string, string, string, string] }
> = {
  viaje: {
    id: 'verde-botella',
    colores: ['#173326', '#2f5d50', '#7fa98a', '#0e2219'],
  },
  colchon: {
    id: 'cobalto',
    colores: ['#12275a', '#24499a', '#7c9ad8', '#0a1636'],
  },
  compra: {
    id: 'ambar',
    colores: ['#4f2c0e', '#94591f', '#dca35a', '#2e1b09'],
  },
  carne: {
    id: 'rosado-ahumado',
    colores: ['#4a2a30', '#8a5560', '#c99aa2', '#2b171b'],
  },
  otra: {
    id: 'transparente',
    colores: ['#5d6a70', '#9aa8ad', '#e3ecee', '#3b4549'],
  },
};

/** Lacre (y tinta del sello) por estación del mes de zarpe: [tinta, brillo]. */
export const LACRES: Record<Estacion, readonly [string, string]> = {
  invierno: ['#26304c', '#4d5f8c'],
  primavera: ['#3d5634', '#6f8f5d'],
  verano: ['#8e2f2a', '#c2584c'],
  otono: ['#94641f', '#c99442'],
};

/** Forma del sello por tipo de meta (F1 §2). */
export const FORMAS_SELLO: Record<IdMeta, FormaSello> = {
  viaje: 'circulo',
  compra: 'cuadrado',
  colchon: 'octogono',
  carne: 'rombo',
  otra: 'ovalo',
};

/** Tope de marcas grabadas en el cuello. */
export const TOPE_MARCAS = 24;
/** Meses a los que la pátina llega al máximo. */
export const MESES_PATINA = 24;

export type EntradaAspecto = {
  clase: IdMeta;
  /** Importe de la meta en céntimos. */
  importe: IntegerAmount;
  /** `AAAA-MM-DD`. */
  zarpoEn: string;
  /** `AAAA-MM-DD`: la llegada, o hoy si aún navega. */
  hasta: string;
  /** Nº de aportaciones (marcas del cuello). */
  aportaciones: number;
  /** Largo de la carta (grosor del rollo). */
  largoCarta: number;
  /** Nombre copiado al zarpar (semilla del borde irregular del sello). */
  nombre: string;
};

export type AspectoBotella = {
  vidrio: { id: IdVidrio; colores: readonly [string, string, string, string] };
  escalon: Escalon;
  /** Medidas del SVG (lienzo de 120 de ancho), como el prototipo. */
  medidas: {
    alto: number;
    altoCuello: number;
    anchoCuello: number;
    anchoCuerpo: number;
    hombro: number;
  };
  meses: number;
  /** 0 (limpia) → 1 (opalina) en MESES_PATINA meses. */
  patina: number;
  nivelPatina: NivelPatina;
  estacion: Estacion;
  lacre: readonly [string, string];
  /** Marcas que se dibujan (≤ TOPE_MARCAS). */
  marcas: number;
  /** Hubo más aportaciones de las que caben: se pinta «…». */
  masMarcas: boolean;
  /** Grosor del rollo de la carta (8-20). */
  rollo: number;
  /** Semilla determinista (FNV-1a del nombre) para irregularidades. */
  semilla: number;
  sello: { forma: FormaSello; tinta: readonly [string, string] };
};

/** Escalón de altura: logarítmico, 5 escalones (≈ 56 · 178 · 562 · 1.778 · 5.623 €). */
export function escalonDeImporte(importe: IntegerAmount): Escalon {
  const euros = importe / 100;
  if (!(euros > 0)) {
    return 0;
  }
  const paso = Math.round(Math.log10(euros / 100) * 2);
  return Math.max(0, Math.min(4, paso)) as Escalon;
}

/** Estación de un mes (`AAAA-MM…`): dic-feb invierno, mar-may primavera… */
export function estacionDe(fecha: string): Estacion {
  const m = Number(fecha.slice(5, 7)) - 1;
  if (m === 11 || m < 2) {
    return 'invierno';
  }
  if (m < 5) {
    return 'primavera';
  }
  return m < 8 ? 'verano' : 'otono';
}

/** Meses de calendario entre dos días (los del prototipo: solo año y mes). */
export function mesesEntre(desde: string, hasta: string): number {
  return Math.max(
    0,
    monthUtils.differenceInCalendarMonths(hasta.slice(0, 7), desde.slice(0, 7)),
  );
}

export function nivelDePatina(patina: number): NivelPatina {
  if (patina < 0.2) {
    return 'limpia';
  }
  return patina < 0.5 ? 'ligera' : 'opalina';
}

/** FNV-1a de 32 bits (igual que `hash` del prototipo). */
export function semillaDe(texto: string): number {
  let h = 2166136261;
  for (const c of texto) {
    h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  }
  return h >>> 0;
}

export function aspectoBotella(e: EntradaAspecto): AspectoBotella {
  const escalon = escalonDeImporte(e.importe);
  const meses = mesesEntre(e.zarpoEn, e.hasta);
  const patina = Math.min(meses / MESES_PATINA, 1);
  const estacion = estacionDe(e.zarpoEn);
  const lacre = LACRES[estacion];
  const aportaciones = Math.max(0, Math.floor(e.aportaciones));
  return {
    vidrio: VIDRIOS[e.clase],
    escalon,
    medidas: {
      alto: 150 + escalon * 30,
      altoCuello: 22 + escalon * 14,
      anchoCuello: 36 - escalon * 4,
      anchoCuerpo: 100 - escalon * 6,
      hombro: 18 + escalon * 6,
    },
    meses,
    patina,
    nivelPatina: nivelDePatina(patina),
    estacion,
    lacre,
    marcas: Math.min(aportaciones, TOPE_MARCAS),
    masMarcas: aportaciones > TOPE_MARCAS,
    rollo: Math.max(8, Math.min(20, 8 + e.largoCarta / 22)),
    semilla: semillaDe(e.nombre),
    sello: { forma: FORMAS_SELLO[e.clase], tinta: lacre },
  };
}

/** Aspecto de un recuerdo: meta y aportaciones de la llegada si la hay. */
export function aspectoDeRecuerdo(r: Recuerdo, hoy: string): AspectoBotella {
  return aspectoBotella({
    clase: r.base.clase,
    importe: r.llegada?.meta ?? r.base.metaAlZarpar,
    zarpoEn: r.base.zarpoEn,
    hasta: r.llegada?.fecha ?? hoy,
    aportaciones: r.llegada?.aportaciones ?? 0,
    largoCarta: r.base.carta.length,
    nombre: r.base.nombre,
  });
}

const ROMANOS = [
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
];

/** Texto en arco del sello: «LISBOA · 19 · X · 2026» (nombre y día de llegada). */
export function textoSello(nombre: string, fecha: string): string {
  const [anio, mes, dia] = fecha.split('-').map(Number);
  return `${nombre.toLocaleUpperCase()} · ${dia} · ${ROMANOS[mes - 1] ?? ''} · ${anio}`;
}

// ---------------------------------------------------------------------------
// Textos explicativos (la «lectura» de cada atributo en la ficha)
// ---------------------------------------------------------------------------

export type ExplicacionAspecto = {
  vidrio: string;
  altura: string;
  patina: string;
  lacre: string;
  marcas: string;
  sello: string;
};

/**
 * Lectura literal de cada atributo («Verde botella: viaje», «900 €: botella
 * mediana»…). `importe` llega ya formateado por quien llama.
 */
export function explicarAspecto(
  t: TFunction,
  a: AspectoBotella,
  importe: string,
  aportaciones: number,
): ExplicacionAspecto {
  return {
    vidrio: textoVidrio(t, a.vidrio.id),
    altura: textoAltura(t, a.escalon, importe),
    patina: textoPatina(t, a.nivelPatina, a.meses),
    lacre: textoLacre(t, a.estacion),
    marcas: a.masMarcas
      ? t('{{count}} contributions: the neck has no room for more', {
          count: aportaciones,
        })
      : t('{{count}} contributions engraved on the neck', {
          count: aportaciones,
        }),
    sello: textoSelloForma(t, a.sello.forma),
  };
}

function textoVidrio(t: TFunction, id: IdVidrio): string {
  switch (id) {
    case 'verde-botella':
      return t('Bottle green: a trip');
    case 'cobalto':
      return t('Cobalt: emergency fund');
    case 'ambar':
      return t('Amber: a big purchase');
    case 'rosado-ahumado':
      return t('Smoky pink: driving licence');
    default:
      return t('Clear glass: another goal');
  }
}

function textoAltura(t: TFunction, escalon: Escalon, importe: string): string {
  switch (escalon) {
    case 0:
      return t('{{amount}}: small jar', { amount: importe });
    case 1:
      return t('{{amount}}: short bottle', { amount: importe });
    case 2:
      return t('{{amount}}: medium bottle', { amount: importe });
    case 3:
      return t('{{amount}}: tall bottle', { amount: importe });
    default:
      return t('{{amount}}: long-necked bottle', { amount: importe });
  }
}

function textoPatina(t: TFunction, nivel: NivelPatina, meses: number): string {
  if (meses < 1) {
    return t('Less than a month at sea: clean glass');
  }
  switch (nivel) {
    case 'limpia':
      return t('{{count}} months at sea: clean glass', { count: meses });
    case 'ligera':
      return t('{{count}} months at sea: light patina', { count: meses });
    default:
      return t('{{count}} months at sea: opaline patina', { count: meses });
  }
}

function textoLacre(t: TFunction, estacion: Estacion): string {
  switch (estacion) {
    case 'invierno':
      return t('Set sail in winter: night-blue sealing wax');
    case 'primavera':
      return t('Set sail in spring: green sealing wax');
    case 'verano':
      return t('Set sail in summer: vermilion sealing wax');
    default:
      return t('Set sail in autumn: ochre sealing wax');
  }
}

function textoSelloForma(t: TFunction, forma: FormaSello): string {
  switch (forma) {
    case 'circulo':
      return t('Round stamp: a trip');
    case 'cuadrado':
      return t('Square stamp: a big purchase');
    case 'octogono':
      return t('Octagonal stamp: emergency fund');
    case 'rombo':
      return t('Diamond stamp: driving licence');
    default:
      return t('Oval stamp: another goal');
  }
}

/**
 * Ritmo frente al plazo para la cartela (informativo, nunca en rojo).
 * `dias` = días de la llegada respecto al plazo (negativo = antes).
 */
export function textoRitmo(t: TFunction, dias: number): string {
  const a = Math.abs(dias);
  if (a < 4) {
    return t('On the planned date');
  }
  const antes = dias < 0;
  if (a >= 28) {
    const count = Math.round(a / 30) || 1;
    return antes
      ? t('{{count}} months ahead of plan', { count })
      : t('{{count}} months later than planned', { count });
  }
  const count = Math.round(a / 7) || 1;
  return antes
    ? t('{{count}} weeks ahead of plan', { count })
    : t('{{count}} weeks later than planned', { count });
}
